// Segmented 6-digit code input — auto-advance, backspace, full-code paste.
import { useRef } from 'react';

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  /** 'glass' for auth screens (dark gradient), 'paper' for light surfaces. */
  variant?: 'glass' | 'paper';
}

const LENGTH = 6;

export function CodeInput({ value, onChange, onComplete, disabled, variant = 'glass' }: CodeInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? '');

  function commit(next: string) {
    const clean = next.replace(/\D/g, '').slice(0, LENGTH);
    onChange(clean);
    if (clean.length === LENGTH) onComplete?.(clean);
  }

  function handleChange(i: number, raw: string) {
    const ch = raw.replace(/\D/g, '');
    if (!ch) return;
    const next = value.slice(0, i) + ch + value.slice(i + ch.length);
    commit(next);
    const focusTo = Math.min(i + ch.length, LENGTH - 1);
    refs.current[focusTo]?.focus();
  }

  function handleKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) {
        commit(value.slice(0, i) + value.slice(i + 1));
      } else if (i > 0) {
        commit(value.slice(0, i - 1));
        refs.current[i - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < LENGTH - 1) {
      refs.current[i + 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    commit(e.clipboardData.getData('text'));
  }

  const boxClass =
    variant === 'glass'
      ? 'glass-input text-white focus:border-brand-rose focus:ring-[3px] focus:ring-brand-rose/25'
      : 'border border-slate-200 bg-white text-ink focus:border-brand-rose focus:ring-[3px] focus:ring-brand-rose/15';

  return (
    <div className="flex gap-2 justify-between" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          aria-label={`Digit ${i + 1}`}
          className={`w-12 h-14 rounded-lg text-center text-xl font-semibold focus:outline-none transition-all disabled:opacity-50 ${boxClass}`}
        />
      ))}
    </div>
  );
}
